import { useEffect, useState } from "react";
import { Coffee, Gamepad2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useStore } from "@/context/StoreContext";

const JAVA_RE = /^[A-Za-z0-9_]{3,16}$/;
const BEDROCK_RE = /^[A-Za-z0-9_ ]{1,15}$/;

export const UsernameDialog = ({ pkg, onClose }) => {
  const { username, setUsername, platform, setPlatform, addToBasket, setBasketOpen } = useStore();
  const [value, setValue] = useState(username);
  const [error, setError] = useState("");

  useEffect(() => {
    if (pkg) {
      setValue(username);
      setError("");
    }
  }, [pkg, username]);

  const confirm = () => {
    const trimmed = value.trim();
    const ok = platform === "bedrock" ? BEDROCK_RE.test(trimmed) : JAVA_RE.test(trimmed);
    if (!ok) {
      setError(platform === "bedrock"
        ? "Enter a valid Bedrock gamertag (1-15 letters, numbers, spaces or _)"
        : "Enter a valid Minecraft username (3-16 letters, numbers or _)");
      return;
    }
    setUsername(trimmed);
    addToBasket(pkg);
    toast.success(`${pkg.name} added to basket`, { description: `Delivering to ${trimmed}${platform === "bedrock" ? " (Bedrock)" : ""}` });
    onClose();
    setBasketOpen(true);
  };

  return (
    <Dialog open={!!pkg} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="bg-[#0D0B12] border-white/10 text-white sm:max-w-md" data-testid="username-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Who is this for?</DialogTitle>
          <DialogDescription className="text-slate-400">
            {pkg ? <>Enter the Minecraft username that should receive <span className="text-[#A78BFA] font-semibold">{pkg.name}</span>.</> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-4">
          <div className="grid grid-cols-2 gap-2" data-testid="username-platform-toggle">
            {[
              { value: "java", label: "JAVA", icon: Coffee },
              { value: "bedrock", label: "BEDROCK", icon: Smartphone },
            ].map((p) => {
              const on = platform === p.value;
              return (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => { setPlatform(p.value); setError(""); }}
                  data-testid={`username-platform-${p.value}`}
                  className={`flex items-center justify-center gap-2 py-2 rounded-xl text-[11px] font-bold tracking-[0.15em] border transition-[background-color,border-color,color] duration-300 ${
                    on
                      ? "bg-[#8B5CF6] border-[#8B5CF6] text-white"
                      : "bg-white/5 border-white/10 text-slate-400 hover:text-white"
                  }`}
                >
                  <p.icon className="w-3.5 h-3.5" /> {p.label}
                </button>
              );
            })}
          </div>
          <div className="relative">
            <Gamepad2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input
              value={value}
              onChange={(e) => { setValue(e.target.value); setError(""); }}
              onKeyDown={(e) => e.key === "Enter" && confirm()}
              placeholder={platform === "bedrock" ? "e.g. Steve 2024" : "e.g. Steve_2024"}
              data-testid="username-input"
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-slate-600 focus-visible:ring-[#8B5CF6]"
            />
          </div>
          {platform === "bedrock" && (
            <p className="text-[11px] text-slate-500" data-testid="username-bedrock-hint">
              Delivered via Bedrock prefix as <span className="text-[#A78BFA] font-semibold">.{(value.trim() || "YourName").replace(/ /g, "_")}</span>
            </p>
          )}
          {error && <p className="text-sm text-red-400" data-testid="username-error">{error}</p>}
          <button
            onClick={confirm}
            data-testid="username-confirm-button"
            className="w-full py-3 rounded-xl bg-[#8B5CF6] hover:bg-[#7C3AED] text-white text-sm font-bold tracking-[0.15em] transition-[background-color] duration-300"
          >
            ADD TO BASKET
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
